import type { FontFamily } from '@/api/types/font-family'
import { logger } from '@/utils/logger'

const loadedKeys = new Set<string>()

const MAX_CONCURRENT_LOADS = 25

function faceKey(familyName: string, fontId: number): string {
  return `${familyName}:${fontId}`
}

export type FontLoadResult = {
  failedFonts: Array<{ familyName: string; fontId: number }>
  /**
   * Faces newly added to `document.fonts` by THIS call — 0 when every requested face was already
   * registered (see `loadedKeys`).
   *
   * A newly-available face changes every text metric in the document, so any PRE-MEASURED wrap or
   * height result captured before it arrived is stale. Reported so `FontProvider` can bump an
   * invalidation counter exactly once per real load, and stay silent on a no-op call. Previously
   * only `failedFonts` came back, so a fully successful load produced NO observable state change
   * and display mode's memoized row splits stayed frozen at the fallback font's metrics forever
   * while edit mode re-measured on entry with the real face.
   *
   * Known, bounded imprecision: two concurrent calls for the same not-yet-loaded family can both
   * build a pending face (`loadedKeys.add` only happens post-load), producing two bumps instead of
   * one. Harmless — a bump only invalidates memos — and it settles once the spread's fonts are in.
   */
  loadedCount: number
}

/**
 * Register @font-face entries for all variants so CSS can select by family + weight + style.
 * Loads up to MAX_CONCURRENT_LOADS fonts in parallel to avoid serial waterfalls.
 * Returns information about any fonts that failed to load, plus how many newly landed.
 */
export async function loadFontFacesForFamilies(
  families: FontFamily[],
  options?: { signal?: AbortSignal }
): Promise<FontLoadResult> {
  const signal = options?.signal
  const failedFonts: Array<{ familyName: string; fontId: number }> = []
  let loadedCount = 0

  type PendingFace = { face: FontFace; key: string; familyName: string; fontId: number }

  const pending: PendingFace[] = []

  for (const family of families) {
    for (const font of family.fonts ?? []) {
      if (!font.woffFilePath) continue
      const key = faceKey(family.familyName, font.fontId)

      if (loadedKeys.has(key)) continue

      const weight = font.bold ? '700' : '400'
      const style = font.italic ? 'italic' : 'normal'

      const face = new FontFace(family.familyName, `url(${font.woffFilePath})`, {
        weight,
        style,
        display: 'swap',
      })

      pending.push({ face, key, familyName: family.familyName, fontId: font.fontId })
    }
  }

  for (let i = 0; i < pending.length; i += MAX_CONCURRENT_LOADS) {
    if (signal?.aborted) return { failedFonts, loadedCount }

    const batch = pending.slice(i, i + MAX_CONCURRENT_LOADS)

    const results = await Promise.allSettled(batch.map((p) => p.face.load()))

    if (signal?.aborted) return { failedFonts, loadedCount }

    for (let j = 0; j < batch.length; j++) {
      const { face, key, familyName, fontId } = batch[j]

      if (results[j].status === 'fulfilled') {
        document.fonts.add(face)
        loadedKeys.add(key)
        loadedCount++
      } else {
        logger.warn('FontFace load failed', {
          family: familyName,
          fontId,
          error: (results[j] as PromiseRejectedResult).reason,
        })
        failedFonts.push({ familyName, fontId })
      }
    }
  }

  return { failedFonts, loadedCount }
}
